import { Box, Alert as JoyAlert, CircularProgress, Divider } from "@mui/joy";
import { useNavigate, useParams } from "react-router-dom";
import { useState, useEffect } from "react";

import { useManufacturer } from "../../../../hooks/query";
import { useAuthorization } from "../../../../hooks";
import { ROLES } from "../../../../constants/permissions";
import { VerticalTabs, ManufacturerDetails, ProductOnboarding } from "./components";
import Breadcrumb from "./components/Breadcrumb";
import type { IManufacturer } from "../../../../interfaces";

export default function Edit() {
    const navigate = useNavigate();
    const { manufacturerId } = useParams<{ manufacturerId: string }>();
    const { hasRole } = useAuthorization();
    const canManage = hasRole(ROLES.SUPER_ADMIN);
    const { useList, useUpdate, useDelete } = useManufacturer();
    const { data, isLoading, isError, error } = useList();
    const updateManufacturerSvc = useUpdate();
    const deleteManufacturerSvc = useDelete();

    const [activeTab, setActiveTab] = useState<string>("details");
    const [manufacturer, setManufacturer] = useState<IManufacturer | null>(null);

    useEffect(() => {
        if (data && manufacturerId) {
            const manufacturers = (data as IManufacturer[]) || [];
            const found = manufacturers.find((m) => m.Id === manufacturerId);
            if (found) {
                setManufacturer(found);
            }
        }
    }, [data, manufacturerId]);

    async function handleUpdate(updatedData: Partial<IManufacturer>) {
        if (!canManage || !manufacturerId || !manufacturer) return;

        try {
            const params = {
                Id: manufacturerId,
                Name: updatedData.Name,
                ShortName: updatedData.ShortName,
                Headquarters: updatedData.Headquarters ?? undefined,
                Founded: updatedData.Founded ?? undefined,
                Industry: updatedData.Industry ?? undefined,
                Products: updatedData.Products ?? undefined,
                Website: updatedData.Website ?? undefined,
                Status: updatedData.Status ?? undefined,
            };
            await updateManufacturerSvc.mutateAsync(params);
        } catch (err) {
            console.error("Failed to update manufacturer:", err);
        }
    }

    async function handleDelete() {
        if (!canManage || !manufacturerId) return;

        try {
            await deleteManufacturerSvc.mutateAsync(manufacturerId);
            navigate("/admin/manufacture/list");
        } catch (err) {
            console.error("Failed to delete manufacturer:", err);
        }
    }

    if (isLoading) {
        return (
            <Box flex={1} display="flex" justifyContent="center" alignItems="center">
                <CircularProgress />
            </Box>
        );
    }

    if (isError) {
        return (
            <Box flex={1} p={2}>
                <JoyAlert color="danger">
                    {error instanceof Error ? error.message : "Failed to load manufacturer"}
                </JoyAlert>
            </Box>
        );
    }

    if (!manufacturer) {
        return (
            <Box flex={1} p={2}>
                <JoyAlert color="warning">Manufacturer not found</JoyAlert>
            </Box>
        );
    }

    return (
        <Box
            flex={1}
            display={"flex"}
            flexDirection={"column"}
            maxHeight={"100%"}
            sx={{ overflow: "hidden", minWidth: 0 }}
        >
            <Breadcrumb />
            <Divider sx={{ mt: 0, ml: 0 }} />
            <Box
                component="div"
                width="100%"
                height="100%"
                display="flex"
                flex={1}
                sx={{
                    overflow: "hidden",
                    minWidth: 0,
                }}
            >
                <VerticalTabs value={activeTab} onChange={setActiveTab} />
                <Box
                    flex={1}
                    sx={{
                        overflow: "auto",
                        minWidth: 0,
                    }}
                >
                    {activeTab === "details" && (
                        <ManufacturerDetails
                            manufacturer={manufacturer}
                            onUpdate={handleUpdate}
                            onDelete={handleDelete}
                            isUpdating={updateManufacturerSvc.isPending}
                            isDeleting={deleteManufacturerSvc.isPending}
                            canManage={canManage}
                        />
                    )}
                    {activeTab === "products" && (
                        <ProductOnboarding manufacturerId={manufacturer.Id} canManage={canManage} />
                    )}
                </Box>
            </Box>
        </Box>
    );
}
